import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import API_URL from '../config/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

const AdminPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        setIsLoggedIn(true);
      } catch (err) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Save token and user data
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      
      setUser(data.user);
      setIsLoggedIn(true);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsLoggedIn(false);
    setUser(null);
  };

  if (!isLoggedIn) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <SEO
          title="Admin Login | GoodCart"
          description="Login to the GoodCart admin panel"
          url="https://goodcart.org/admin"
        />
        
        <div className="container-custom max-w-md">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Login</h1>
              <p className="text-gray-600">Sign in to access the admin panel</p>
            </div>
            
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6">
                {error}
              </div>
            )}
            
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full btn btn-primary ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-brand-green-600 hover:text-brand-green-800">
                Return to Homepage
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <SEO
        title="Admin Panel | GoodCart"
        description="GoodCart admin panel for managing products and content"
        url="https://goodcart.org/admin"
      />
      
      <div className="container-custom">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 bg-gray-800 text-white">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-bold">GoodCart Admin Panel</h1>
              <div className="flex items-center space-x-4">
                <span className="text-sm">
                  Logged in as <span className="font-semibold">{user?.username}</span>
                </span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Products</h2>
                <p className="text-gray-600 mb-4">Manage your product catalog</p>
                <Link
                  to="/admin/products"
                  className="btn btn-primary w-full"
                >
                  Manage Products
                </Link>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Categories</h2>
                <p className="text-gray-600 mb-4">Organize your product categories</p>
                <Link
                  to="/admin/categories"
                  className="btn btn-primary w-full"
                >
                  Manage Categories
                </Link>
              </div>
              
              <ComplianceToggle />

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Import by ASIN</h2>
                <p className="text-gray-600 mb-4">Quickly add products using a product ASIN</p>
                <Link
                  to="/admin/products"
                  className="btn btn-primary w-full"
                >
                  Go to Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Associates compliance mode toggle (persisted server-side; banner shows site-wide)
const ComplianceToggle: React.FC = () => {
  const [on, setOn] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/config').then(r => r.json()).then(d => setOn(Boolean(d.complianceMode))).catch(() => {});
  }, []);

  const toggle = async () => {
    setBusy(true);
    const token = localStorage.getItem('adminToken');
    try {
      const res = await fetch('/api/admin/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ complianceMode: !on }),
      });
      if (res.ok) setOn(!on);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Associates compliance mode</h2>
      <p className="text-gray-600 mb-4 text-sm">
        Shows the required affiliate disclosure site-wide ("As an Amazon Associate, GoodCart earns from qualifying purchases").
      </p>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-pressed={on}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${on ? 'bg-green-600' : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
        <span className="sr-only">{on ? 'Compliance mode on' : 'Compliance mode off'}</span>
      </button>
      <span className="ml-3 text-sm font-medium text-gray-700">{on ? 'On' : 'Off'}</span>
    </div>
  );
};

export default AdminPage;
